import type { ReactElement } from "react";
import { Unplug } from "lucide-react";
import { Button } from "../atoms/index.js";
import { Card, EmptyState } from "../molecules/index.js";

/**
 * The full-surface notice shown in place of a collaborative canvas when the
 * document is not available — either still connecting, or the session cannot
 * continue.
 *
 * ONE component for every canvas module. SOLVE, VSM and Hoshin each grew their
 * own version of this screen with different markup, different roles and
 * different claims about WHY, which is how a user ended up being told "access
 * was removed" when their access was intact. Copy stays a caller concern (the
 * modules know their own noun and their own phases); the presentation, the ARIA
 * role and the retry affordance do not.
 *
 * `tone` picks the empty state's case. Use `"danger"` when the session has
 * genuinely ended (the error medallion), `"neutral"` while it may still
 * recover — a connecting state must not look like a failure. The notice is a
 * page-level EmptyState `lg` on a Card (OK-33, PR 3).
 *
 * On `role`: a terminal state is an `alert` (it interrupts), a recoverable one
 * is a `status` (it informs). Callers pass `terminal` rather than a raw role so
 * the two can never disagree.
 */
export interface CollabUnavailableNoticeProps {
  /** Short heading — what state the surface is in. */
  readonly title: string;
  /** One or two sentences on what it means for the user, and what happens next. */
  readonly message: string;
  /**
   * True when the session has ended and will not resume on its own. Drives the
   * ARIA role (`alert` vs `status`) and the default tone.
   */
  readonly terminal?: boolean;
  /** Border emphasis. Defaults to `"danger"` when terminal, else `"neutral"`. */
  readonly tone?: "neutral" | "danger";
  /** Rendered only when provided — omit it when there is nothing to retry. */
  readonly onRetry?: (() => void) | undefined;
  /** Test hook so module specs can keep asserting their own surface. */
  readonly testId?: string;
  /** Optional module/entity id echoed as a data attribute for diagnostics. */
  readonly entityId?: string;
}

export function CollabUnavailableNotice({
  title,
  message,
  terminal = false,
  tone,
  onRetry,
  testId,
  entityId,
}: CollabUnavailableNoticeProps): ReactElement {
  const effectiveTone = tone ?? (terminal ? "danger" : "neutral");
  const retry = onRetry ? (
    <Button variant="secondary" onClick={onRetry}>
      Retry
    </Button>
  ) : undefined;
  return (
    <div
      {...(testId === undefined ? {} : { "data-testid": testId })}
      {...(entityId === undefined ? {} : { "data-entity-id": entityId })}
      className="flex min-h-80 flex-1 items-center justify-center bg-pb-page p-6"
      role={terminal ? "alert" : "status"}
    >
      <Card className="w-full max-w-md">
        {effectiveTone === "danger" ? (
          <EmptyState
            variant="error"
            size="lg"
            headingLevel={2}
            title={title}
            description={message}
            action={retry}
          />
        ) : (
          <EmptyState
            size="lg"
            headingLevel={2}
            icon={<Unplug aria-hidden className="h-6 w-6" />}
            title={title}
            description={message}
            action={retry}
          />
        )}
      </Card>
    </div>
  );
}
